import { AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, Renderer2, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subscription, debounceTime } from "rxjs";
import { FilterModelDto } from "src/app/models/filter.model";
import { SortDataColumn } from "src/app/models/table.model";
import { FileNameSetService } from "src/app/services/file-name-set.service";
import { FilterSettingService } from "src/app/services/filter-setting.service";
import { ModalService } from "src/app/services/modal.service";
import { NotificationService } from "src/app/services/notification.service";
import { PermissionService } from "src/app/services/permission.service";
import { SortTableService } from "src/app/services/sort-table.service";
import { TableHeightService } from "src/app/services/table-height.service";
import { FormSelectModelDto } from "../payment-link-list/models/paylinks.model";
import { PaymentModelsDto } from "./models/payment.models";
import { PaymentDataService } from "./services/payment-data-service.service";
import { PaymentDetailInfoService } from "./services/payment-detail-info.service";
import { PaymentStatusService } from "./services/payment-status.service";

@Component({
	selector: "app-payment-list",
	templateUrl: "./payment-list.component.html",
	styleUrls: ["./payment-list.component.scss"],
})
export class PaymentListComponent implements OnInit, OnDestroy, AfterViewInit {
	@ViewChild("tableBlock") tableBlock: ElementRef | undefined;
	@ViewChild("tableBody") tableBody: ElementRef | undefined;
	@ViewChild("paymentBlock") paymentBlock: ElementRef | undefined;

	delta = 150;
	lastPage!: boolean;
	currentPage = 0;

	isOpenFilter = false;

	isOpenPaymentDetail = false;
	isOpenDeletePaymentDialog = false;
	isOpenSuccessDeletePaymentDialog = false;

	subs: Subscription[] = [];

	paymentList: PaymentModelsDto[] = [];

	filterAdditional: FilterModelDto[] | undefined;
	filterStatuses: FilterModelDto[] | undefined;
	filterPaymentTypes: FilterModelDto[] | undefined;
	filterDirections: FilterModelDto[] | undefined;
	filterBanks: FilterModelDto[] | undefined;
	filterCurrencies: FilterModelDto[] | undefined;

	merchantCardSavedList: FormSelectModelDto[] = [];

	sortColumnData: SortDataColumn | undefined;

	fileName = "";

	filterData: any;
	defaultFilterData: any;
	searchWord: string | undefined = "";

	constructor(
		private tableHeightService: TableHeightService,
		private renderer: Renderer2,
		private modalService: ModalService,
		private paymentDataService: PaymentDataService,
		private notificationService: NotificationService,
		private activatedRoute: ActivatedRoute,
		private filterSettingService: FilterSettingService,
		private sortTableService: SortTableService,
		private paymentDetailInfoService: PaymentDetailInfoService,
		private fileNameSetService: FileNameSetService,
		private paymentStatusService: PaymentStatusService,
		public permissionService: PermissionService
	) {}

	ngOnInit() {
		this.initFilter();
		this.initModal();
		this.initCardSavedList();
		this.fileNameInit();
	}

	ngAfterViewInit() {
		this.subs.push(
			this.tableHeightService.tableHeight$.subscribe({
				next: (res) => {
					this.renderer.setStyle(this.tableBlock?.nativeElement, "height", res);
				},
			})
		);
	}

	ngOnDestroy() {
		this.subs.forEach((item) => item.unsubscribe());
		this.sortTableService.sortColum$.next(undefined);
	}

	fileNameInit() {
		this.subs.push(
			this.fileNameSetService.setFileName$.subscribe({
				next: (res) => {
					this.fileName = res;
				},
			})
		);
	}

	getPaymentList() {
		this.lastPage = true;
		this.paymentDataService.loadPaymentList(this.currentPage, this.searchWord, this.filterData).subscribe({
			next: (res) => {
				this.paymentList.push(...res.content);
				this.currentPage++;
				this.lastPage = res.last;
			},
		});
	}

	initCardSavedList() {
		this.subs.push(
			this.paymentDataService.loadCardSavedList().subscribe({
				next: (res) => {
					this.merchantCardSavedList = res["agentCards"];
				},
			})
		);
	}

	initFilter() {
		this.subs.push(
			this.paymentDataService.loadPaymentFilter().subscribe({
				next: (res) => {
					this.filterStatuses = res["statuses"];
					this.filterPaymentTypes = res["paymentTypes"];
					this.filterDirections = res["directions"];
					this.filterBanks = res["banks"];
					this.filterCurrencies = res["currencies"];
					this.filterAdditional = res["additional_params"];

					this.presetFilter();
				},
			})
		);

		this.subs.push(
			this.filterSettingService.filterSetData$.subscribe({
				next: (res) => {
					this.filterData = res;
					this.currentPage = 0;

					this.paymentList = [];
					this.getPaymentList();
				},
			})
		);

		this.subs.push(
			this.filterSettingService.inputSearch$.pipe(debounceTime(300)).subscribe({
				next: (res) => {
					this.paymentDataService.loadSearchList(res).subscribe({
						next: (res) => {
							this.filterSettingService.responseSearchData$.next(res.content);
						},
					});
				},
			})
		);
		this.subs.push(
			this.filterSettingService.filterSearchWord$.subscribe({
				next: (res) => {
					this.searchWord = res;
				},
			})
		);

		this.subs.push(
			this.filterSettingService.filterAllReset$.subscribe({
				next: () => {
					this.searchWord = "";
					this.filterData = undefined;
					this.currentPage = 0;

					this.paymentList = [];
					this.getPaymentList();
				},
			})
		);
	}

	presetFilter() {
		let params = [];
		let filterData = {};

		if (this.activatedRoute.snapshot.queryParamMap.has("terminal_id")) {
			params.push("terminal_id");
		}
		if (this.activatedRoute.snapshot.queryParamMap.has("paylink_token")) {
			params.push("paylink_token");
		}
		if (this.activatedRoute.snapshot.queryParamMap.has("rec_payment_id")) {
			params.push("rec_payment_id");
		}

		if (
			this.activatedRoute.snapshot.queryParamMap.has("paylink_token") ||
			this.activatedRoute.snapshot.queryParamMap.has("terminal_id") ||
			this.activatedRoute.snapshot.queryParamMap.has("rec_payment_id")
		) {
			params.forEach((params) => {
				this.filterAdditional?.find((item) => item.fieldName === params);

				const filter: FilterModelDto = {
					fieldName: params,
					value: this.activatedRoute.snapshot.queryParamMap.get(params) ?? "",
					description: this.activatedRoute.snapshot.queryParamMap.get(params) ?? "",
				};

				if (this.activatedRoute.snapshot.queryParamMap.has("paylink_token")) {
					filterData = {
						paylink_token: this.activatedRoute.snapshot.queryParamMap.get(params) ?? "",
					};
				}

				if (this.activatedRoute.snapshot.queryParamMap.has("terminal_id")) {
					filterData = {
						terminal_id: this.activatedRoute.snapshot.queryParamMap.get(params) ?? "",
					};
				}

				if (this.activatedRoute.snapshot.queryParamMap.has("rec_payment_id")) {
					filterData = {
						rec_payment_id: this.activatedRoute.snapshot.queryParamMap.get(params) ?? "",
					};
				}

				this.sortColumnData = this.sortTableService.sort("ID");

				const sort = `order=${this.sortColumnData?.order || "DESC"}&field=${this.sortColumnData?.field}`;
				this.sortTableService.sortColum$.next(sort);
				this.filterSettingService.filterSet$.next(filter);
				this.filterSettingService.filterSetData$.next(filterData);
				this.isOpenFilter = true;
			});
		} else {
			this.sortColumn("ID", "DESC");
		}
	}

	initModal() {
		this.subs.push(
			this.modalService.isModalOpen$.subscribe({
				next: (res) => {
					if (!res) {
						this.isOpenPaymentDetail = false;
						this.isOpenDeletePaymentDialog = false;
						this.isOpenSuccessDeletePaymentDialog = false;
						this.paymentStatusService.paymentStatusService$.next(null);
					}
				},
			})
		);
	}

	toggleFilter(filterState: boolean) {
		this.isOpenFilter = filterState;
	}

	onScroll(event: any): void {
		const tableBodyHeight = this.tableBody?.nativeElement.clientHeight;
		const tableBlockHeight = this.tableBlock?.nativeElement.clientHeight;
		const scrollTable = event.target.scrollTop;
		if (tableBodyHeight - tableBlockHeight - scrollTable < this.delta && !this.lastPage) {
			this.getPaymentList();
		}
	}

	@HostListener("document:scroll", ["$event"])
	onWindowScroll(event: any): void {
		if (window.innerWidth <= 768) {
			if (this.paymentBlock?.nativeElement.clientHeight - window.scrollY - window.innerHeight + 120 < this.delta && !this.lastPage) {
				this.getPaymentList();
			}
		}
	}

	upload() {
		if (
			(this.filterData?.hasOwnProperty("date_from") && this.filterData?.hasOwnProperty("date_to")) ||
			(!this.filterData?.hasOwnProperty("date_from") && !this.filterData?.hasOwnProperty("date_to"))
		) {
			this.getRegistryData(this.filterData);
		} else {
			this.errorRegistryNotification();
		}
	}

	getRegistryData(filter: any) {
		this.paymentDataService.loadFile(filter).subscribe({
			next: (res) => {
				let link = document.createElement("a");
				link.href = (window.webkitURL || window.URL).createObjectURL(res);
				link.download = this.fileName;
				link.click();

				this.notificationService.setConfiguration(
					"icon-4",
					"success",
					"PAYMENT_PAGE.REGISTER_DOWNLOAD.TITLE_1",
					"PAYMENT_PAGE.REGISTER_DOWNLOAD.DESCRIPTION_1"
				);

				this.notificationService.showNotification();
			},
		});
	}

	errorRegistryNotification() {
		this.notificationService.setConfiguration(
			"icon-6",
			"error",
			"PAYMENT_PAGE.REGISTER_DOWNLOAD.TITLE_2",
			"PAYMENT_PAGE.REGISTER_DOWNLOAD.DESCRIPTION_2"
		);

		this.notificationService.showNotification();
	}

	openModalDetail(id: number) {
		this.notificationService.isNotificationShow$.next(false);
		this.paymentDataService.loadDetailPayment(id).subscribe({
			next: (res) => {
				this.paymentDetailInfoService.paymentDetail$.next(res);
				this.isOpenPaymentDetail = true;
			},
		});
	}

	openModalDetailMobile(id: number) {
		if (window.innerWidth > 768) {
			return;
		} else {
			this.openModalDetail(id);
		}
	}

	openModalDeletePayment(id: number) {
		this.paymentDataService.loadDetailPayment(id).subscribe({
			next: (res) => {
				this.paymentDetailInfoService.paymentDetail$.next(res);
				this.isOpenDeletePaymentDialog = true;
			},
		});
	}

	stopProp(event: MouseEvent) {
		event.stopPropagation();
	}

	copyDetail() {
		this.modalService.isModalOpen$.next(false);

		this.notificationService.setConfiguration(
			"icon-2",
			"warning",
			"PAYMENT_PAGE.NOTIFICATION.INFO_COPIED",
			"PAYMENT_PAGE.NOTIFICATION.DETAIL_PAYMENT_COPIED"
		);

		this.notificationService.showNotification();
	}

	sortColumn(column: string, order: "ASC" | "DESC" | "" = "") {
		this.sortColumnData = this.sortTableService.sort(column);

		const sort = `order=${order || this.sortColumnData?.order}&field=${this.sortColumnData?.field}`;

		if (order) {
			this.sortColumnData!["order"] = order;
		}

		this.sortTableService.sortColum$.next(sort);

		this.currentPage = 0;
		this.paymentList = [];
		this.getPaymentList();
	}

	p2pNotification() {
		this.notificationService.setConfiguration("icon-5", "error", "PAYMENT_PAGE.NOTIFICATION_P2P.TITLE", "PAYMENT_PAGE.NOTIFICATION_P2P.DESCRIPTION");

		this.notificationService.showNotification();
	}

	refundSuccessNotification() {
		this.currentPage = 0;
		this.paymentList = [];
		this.getPaymentList();

		this.notificationService.setConfiguration(
			"icon-3",
			"success",
			"PAYMENT_PAGE.NOTIFICATION_SUCCESS.TITLE",
			"PAYMENT_PAGE.NOTIFICATION_SUCCESS.DESCRIPTION"
		);

		this.notificationService.showNotification();
	}

	fileNotDownloadNotification() {
		this.notificationService.setConfiguration("icon-6", "error", "PAYMENT_PAGE.NOT_DOWNLOAD.TITLE", [
			"PAYMENT_PAGE.NOT_DOWNLOAD.DESCRIPTION",
			"PAYMENT_PAGE.NOT_DOWNLOAD.DESCRIPTION_1",
			"PAYMENT_PAGE.NOT_DOWNLOAD.DESCRIPTION_2",
			"PAYMENT_PAGE.NOT_DOWNLOAD.DESCRIPTION_3",
		]);

		this.notificationService.showNotification();
	}

	statusUpdatedNotification() {
		this.notificationService.setConfiguration(
			"icon-2",
			"warning",
			"PAYMENT_PAGE.NOTIFICATION_UPDATE_STATUS.TITLE",
			"PAYMENT_PAGE.NOTIFICATION_UPDATE_STATUS.DESCRIPTION"
		);

		this.notificationService.showNotification();
	}
}
